import {
  getHrefWithThreadPreservation,
  RelativeTime,
  Badge,
  EntityLogo,
  EMPLOYMENT_TYPE_LABELS,
  getPublicationStatusBadge,
  WORK_MODE_LABELS,
  formatRelativeTimeInDays,
  getPluginSlots,
  cn,
} from '@yeshuman/sdk'
import type { ListCardHeaderBadge, ResolvedListingProvider } from '@yeshuman/sdk'
import { Fragment, type ReactNode } from 'react'
import { Link } from 'react-router-dom'


import {
  formatOpportunityJobPlace,
  formatOpportunitySalaryRange,
  type OpportunityJobSummaryInput,
} from '../utils/opportunity-job-meta'
import { ListingProviderIndicator } from './listing-provider-indicator'

/** Job-board header: 2 columns on `@md+`, stacked on narrow viewports. */
export const JOB_OPENING_TWO_COL_ROW =
  'flex w-full min-w-0 flex-col gap-4 @md:flex-row @md:items-start @md:justify-between'

/** Job-board footer: optional salary/note left; trailing cluster uses ml-auto to anchor end. */
export const JOB_OPENING_FOOTER_ROW =
  'flex w-full min-w-0 flex-row flex-wrap items-center gap-3'

export type JobOpeningParsedMeta = {
  employmentType: string
  locationText: string
  workMode: string
  locationWorkModeText: string
  salaryText: string
}

export function parseJobOpeningMetaFields(
  meta?: OpportunityJobSummaryInput | null,
): JobOpeningParsedMeta {
  const employmentType =
    meta?.employment_type && EMPLOYMENT_TYPE_LABELS[meta.employment_type]
      ? EMPLOYMENT_TYPE_LABELS[meta.employment_type]
      : ''
  const workMode =
    meta?.work_mode && WORK_MODE_LABELS[meta.work_mode] ? WORK_MODE_LABELS[meta.work_mode] : ''
  const locationText = meta ? formatOpportunityJobPlace(meta) : ''
  const locationWorkModeText = [locationText, workMode].filter(Boolean).join(' · ')
  const salaryText = meta ? (formatOpportunitySalaryRange(meta) ?? '') : ''

  return { employmentType, locationText, workMode, locationWorkModeText, salaryText }
}

export function hasJobOpeningMetaContent(
  meta?: OpportunityJobSummaryInput | null,
  options?: {
    showTimestamp?: boolean
    publishedAt?: string | null
    updatedAt?: string | null
    parsed?: Pick<JobOpeningParsedMeta, 'employmentType' | 'locationWorkModeText'>
  },
): boolean {
  const { employmentType, locationWorkModeText } =
    options?.parsed ?? parseJobOpeningMetaFields(meta)
  const showTimestamp = options?.showTimestamp ?? true
  const publishedAtValue = options?.publishedAt?.trim() ?? ''
  const updatedAtValue = options?.updatedAt?.trim() ?? ''

  return Boolean(
    locationWorkModeText ||
      employmentType ||
      (showTimestamp && (publishedAtValue || updatedAtValue)),
  )
}

export type JobOpeningIdentityPresentation = 'compact' | 'detail' | 'public'

const JOB_OPENING_IDENTITY_LIST_BASE = {
  logoSize: 'thumb' as const,
  logoClassName: undefined,
  rowGap: 'gap-3',
}

const JOB_OPENING_IDENTITY_PRESENTATION = {
  compact: {
    ...JOB_OPENING_IDENTITY_LIST_BASE,
    titleClassName: 'min-w-0 w-full break-words text-lg leading-none font-semibold',
    titleElement: 'h3' as const,
    textGap: 'gap-[3px]',
  },
  detail: {
    ...JOB_OPENING_IDENTITY_LIST_BASE,
    titleClassName: 'min-w-0 w-full break-words text-2xl font-semibold tracking-tight',
    titleElement: 'h1' as const,
    textGap: 'gap-1',
  },
  public: {
    logoSize: 'thumb' as const,
    logoClassName: 'size-16 sm:size-20',
    titleClassName:
      'min-w-0 w-full break-words text-2xl font-semibold tracking-tight sm:text-3xl',
    titleElement: 'h1' as const,
    textGap: 'gap-1.5',
    rowGap: 'gap-4',
  },
} satisfies Record<
  JobOpeningIdentityPresentation,
  {
    logoSize: 'thumb' | 'hero' | 'editor'
    logoClassName?: string
    titleClassName: string
    titleElement: 'h3' | 'h1'
    textGap: string
    rowGap: string
  }
>

export type JobOpeningIdentityHeaderProps = {
  title: string
  /** `compact` — list cards; `detail` — agent-shell job detail; `public` — share/marketing job page. */
  presentation?: JobOpeningIdentityPresentation
  publicationStatus?: string | null
  showPublicationBadge?: boolean
  promotionBadge?: ListCardHeaderBadge | null
  organisationLabel?: string | null
  organisationLogoUrl?: string | null
  /** When set, organisation name links to the company profile (plain text styling + hover). */
  organisationHref?: string | null
  organisationLinkState?: unknown
  /** Detail surfaces: edit or other actions beside the status badge (in flow, not floated). */
  trailing?: ReactNode
  /** Listing source platform (Adzuna, TalentCo, …) — separate from employer thumb. */
  listingProvider?: ResolvedListingProvider | null
}

export function JobOpeningIdentityHeader({
  title,
  presentation = 'compact',
  publicationStatus,
  showPublicationBadge = true,
  promotionBadge = null,
  organisationLabel,
  organisationLogoUrl,
  organisationHref,
  organisationLinkState,
  trailing,
  listingProvider,
}: JobOpeningIdentityHeaderProps) {
  const orgLabel = organisationLabel?.trim() || ''
  const orgHref = organisationHref?.trim() || ''
  const logoSrc = organisationLogoUrl?.trim() ?? ''
  const badgeResolved = showPublicationBadge
    ? getPublicationStatusBadge(publicationStatus)
    : promotionBadge ?? null
  const hasTrailing = trailing != null
  const styles = JOB_OPENING_IDENTITY_PRESENTATION[presentation]
  const TitleTag = styles.titleElement

  return (
    <div className={JOB_OPENING_TWO_COL_ROW}>
      <div className={cn('flex min-w-0 flex-1 items-start', styles.rowGap)}>
        <EntityLogo
          src={logoSrc || null}
          alt=""
          size={styles.logoSize}
          className={styles.logoClassName}
        />
        <div className={cn('flex min-w-0 flex-col', styles.textGap)}>
          <TitleTag className={styles.titleClassName}>{title}</TitleTag>
          {orgLabel ? (
            orgHref ? (
              <Link
                to={getHrefWithThreadPreservation(orgHref)}
                state={organisationLinkState}
                className="w-fit min-w-0 break-words text-sm text-muted-foreground cursor-pointer underline-offset-2 transition-colors hover:text-foreground hover:underline"
              >
                {orgLabel}
              </Link>
            ) : (
              <p className="min-w-0 break-words text-sm text-muted-foreground">{orgLabel}</p>
            )
          ) : null}
          {listingProvider ? (
            <ListingProviderIndicator
              label={listingProvider.label}
              logoUrl={listingProvider.logoUrl}
              href={listingProvider.href}
              variant="inline"
            />
          ) : null}
        </div>
      </div>
      {badgeResolved || hasTrailing ? (
        <div
          className={cn(
            'flex shrink-0 flex-wrap items-center gap-2 self-start',
            !badgeResolved && hasTrailing && 'w-full justify-end @md:w-auto @md:justify-start',
          )}
        >
          {badgeResolved ? (
            <Badge variant={badgeResolved.variant ?? 'secondarySoft'}>
              {badgeResolved.label}
            </Badge>
          ) : null}
          {hasTrailing ? <div className="shrink-0">{trailing}</div> : null}
        </div>
      ) : null}
    </div>
  )
}

export type JobOpeningMetaLineProps = {
  meta?: OpportunityJobSummaryInput | null
  locationText?: string
  workMode?: string
  /** When set with `employmentType`, used if location/work mode are not passed separately. */
  locationWorkModeText?: string
  employmentType?: string
  publishedAt?: string | null
  updatedAt?: string | null
  /** Omit the posted/updated segment (compact teaser cards). Default true. */
  showTimestamp?: boolean
  /**
   * `list` — static day bands, no per-row tooltip (job cards).
   * `interactive` — RelativeTime tooltip (detail surfaces).
   */
  timestampVariant?: 'list' | 'interactive'
}

/**
 * One wrapping muted row — `location · work mode · employment type · posted` —
 * shared by job cards and detail surfaces. On TalentCo, location, work mode,
 * and employment type are semibold; posted/updated stays regular. No per-datum icons.
 */
export function JobOpeningMetaLine({
  meta,
  locationText: locationTextProp,
  workMode: workModeProp,
  locationWorkModeText: locationWorkModeTextProp,
  employmentType: employmentTypeProp,
  publishedAt,
  updatedAt,
  showTimestamp = true,
  timestampVariant = 'list',
}: JobOpeningMetaLineProps) {
  const parsed =
    locationTextProp === undefined &&
    workModeProp === undefined &&
    employmentTypeProp === undefined &&
    locationWorkModeTextProp === undefined
      ? parseJobOpeningMetaFields(meta)
      : null
  const locationText = locationTextProp ?? parsed?.locationText ?? ''
  const workMode = workModeProp ?? parsed?.workMode ?? ''
  const employmentType = employmentTypeProp ?? parsed?.employmentType ?? ''
  const locationWorkModeText =
    locationWorkModeTextProp ??
    ([locationText, workMode].filter(Boolean).join(' · ') || parsed?.locationWorkModeText || '')
  const publishedAtValue = publishedAt?.trim() ?? ''
  const updatedAtValue = updatedAt?.trim() ?? ''
  const emphasizeFacts = getPluginSlots().emphasizeJobFacts

  const segments: { content: ReactNode; emphasize: boolean }[] = []
  if (emphasizeFacts && (locationText || workMode)) {
    if (locationText) segments.push({ content: locationText, emphasize: true })
    if (workMode) segments.push({ content: workMode, emphasize: true })
  } else if (locationWorkModeText) {
    segments.push({ content: locationWorkModeText, emphasize: false })
  }
  if (employmentType) segments.push({ content: employmentType, emphasize: emphasizeFacts })
  if (showTimestamp) {
    if (publishedAtValue) {
      segments.push({
        content:
          timestampVariant === 'interactive' ? (
            <RelativeTime value={publishedAtValue} prefix="Posted" unit="days" tickMs={0} />
          ) : (
            `Posted ${formatRelativeTimeInDays(publishedAtValue)}`
          ),
        emphasize: false,
      })
    } else if (updatedAtValue) {
      segments.push({
        content: `Updated ${new Date(updatedAtValue).toLocaleDateString()}`,
        emphasize: false,
      })
    }
  }

  if (segments.length === 0) return null

  return (
    <p className="flex min-w-0 flex-wrap items-center gap-x-1.5 gap-y-1 text-sm text-muted-foreground">
      {segments.map((segment, index) => (
        <Fragment key={index}>
          {index > 0 ? <span aria-hidden>·</span> : null}
          <span className={cn('min-w-0 break-words', segment.emphasize && 'font-semibold')}>
            {segment.content}
          </span>
        </Fragment>
      ))}
    </p>
  )
}

export type JobOpeningSalaryFooterProps = {
  meta?: OpportunityJobSummaryInput | null
  /** Preformatted salary (e.g. public job API `salary_display`) when structured fields are absent. */
  salaryLabel?: string | null
  footerNote?: string | null
  trailing?: ReactNode
}

export function JobOpeningSalaryFooter({
  meta,
  salaryLabel,
  footerNote,
  trailing,
}: JobOpeningSalaryFooterProps) {
  const salaryFromLabel = salaryLabel?.trim() || ''
  const salaryText =
    salaryFromLabel || (meta ? parseJobOpeningMetaFields(meta).salaryText : '')
  const trimmedFooterNote = footerNote?.trim() ?? ''
  const hasFooterLeft = Boolean(salaryText || trimmedFooterNote)
  const hasTrailing = trailing != null

  if (!hasFooterLeft && !hasTrailing) return null

  return (
    <div className={JOB_OPENING_FOOTER_ROW}>
      {hasFooterLeft ? (
        <div className="min-w-0 space-y-1">
          {salaryText ? (
            <p className="text-sm font-semibold text-foreground">{salaryText}</p>
          ) : null}
          {trimmedFooterNote ? (
            <p className="text-xs text-muted-foreground">{trimmedFooterNote}</p>
          ) : null}
        </div>
      ) : null}
      {hasTrailing ? <div className="ml-auto shrink-0">{trailing}</div> : null}
    </div>
  )
}
